import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { adminGetFamilies, formatCurrency } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Search, Users, ArrowRight, ShieldCheck, Lock, Home as HomeIcon, Activity, RefreshCw,
  ArrowUpDown,
} from 'lucide-react';

const AdminFamiliesPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('last_activity'); // last_activity | tx_count | balance | created_at
  const [sortDir, setSortDir] = useState('desc');

  const load = async () => {
    setLoading(true);
    try {
      const res = await adminGetFamilies();
      setFamilies(res.data);
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Solo admin');
        navigate('/');
      } else {
        toast.error('Errore caricamento');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const sorted = useMemo(() => {
    const filter = search.toLowerCase();
    let list = families;
    if (filter) {
      list = list.filter((f) =>
        (f.code || '').toLowerCase().includes(filter) ||
        (f.name || '').toLowerCase().includes(filter) ||
        f.members.some((m) => (m.email || '').toLowerCase().includes(filter) || (m.name || '').toLowerCase().includes(filter))
      );
    }
    const sortField = {
      last_activity: (f) => f.last_activity || '0000-00-00',
      tx_count: (f) => f.transaction_count,
      balance: (f) => f.balance_lifetime,
      created_at: (f) => f.created_at || '',
    }[sortBy];
    list = [...list].sort((a, b) => {
      const va = sortField(a), vb = sortField(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [families, search, sortBy, sortDir]);

  if (user?.role !== 'admin') {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <Lock className="w-12 h-12 text-muted-foreground mb-3" strokeWidth={1.5} />
        <h2 className="text-xl font-semibold mb-1">Area riservata</h2>
        <p className="text-muted-foreground">Solo gli account admin.</p>
      </div>
    );
  }

  const sortHeader = (label, field) => (
    <button
      onClick={() => {
        if (sortBy === field) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
        else { setSortBy(field); setSortDir('desc'); }
      }}
      className={`inline-flex items-center gap-1 hover:text-foreground transition-colors ${
        sortBy === field ? 'text-foreground' : ''
      }`}
    >
      {label}
      <ArrowUpDown className="w-3 h-3 opacity-50" />
    </button>
  );

  return (
    <div className="space-y-6 animate-in" data-testid="admin-families-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="w-5 h-5 text-primary" />
            <Badge className="bg-primary/10 text-primary border-0">ADMIN</Badge>
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Famiglie</h1>
          <p className="text-muted-foreground">{families.length} famiglie nel sistema</p>
        </div>
        <Button variant="outline" onClick={load}>
          <RefreshCw className="w-4 h-4 mr-2" /> Aggiorna
        </Button>
      </div>

      <Card>
        <CardContent className="pt-5">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Cerca per codice famiglia, nome, email membro…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              data-testid="search-families-input"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Caricamento…</div>
          ) : sorted.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">Nessuna famiglia trovata</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-muted-foreground border-b">
                    <th className="py-3 px-4 font-medium">Famiglia</th>
                    <th className="py-3 px-4 font-medium">Membri</th>
                    <th className="py-3 px-4 font-medium text-right">{sortHeader('Transazioni', 'tx_count')}</th>
                    <th className="py-3 px-4 font-medium text-right">{sortHeader('Saldo lifetime', 'balance')}</th>
                    <th className="py-3 px-4 font-medium">{sortHeader('Ultima attività', 'last_activity')}</th>
                    <th className="py-3 px-4 font-medium">{sortHeader('Creata', 'created_at')}</th>
                    <th className="py-3 px-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((f) => (
                    <tr
                      key={f.id}
                      onClick={() => navigate(`/admin/families/${f.id}`)}
                      className="border-b last:border-0 hover:bg-muted/40 transition-colors cursor-pointer"
                      data-testid={`family-row-${f.id}`}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <HomeIcon className="w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                          <div>
                            <p className="font-mono font-medium">{f.code}</p>
                            {f.name && <p className="text-xs text-muted-foreground">{f.name}</p>}
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-1.5">
                          <Users className="w-3.5 h-3.5 text-muted-foreground" />
                          <span className="tabular-nums">{f.member_count}</span>
                          {f.members.some((m) => m.role === 'admin') && (
                            <Badge className="text-[9px] bg-primary/10 text-primary border-0 ml-1">admin</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground truncate max-w-[280px] mt-0.5">
                          {f.members.map((m) => m.email).join(', ') || '—'}
                        </p>
                      </td>
                      <td className="py-3 px-4 text-right tabular-nums font-semibold">{f.transaction_count}</td>
                      <td className={`py-3 px-4 text-right tabular-nums font-semibold ${
                        f.balance_lifetime >= 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'
                      }`}>
                        {formatCurrency(f.balance_lifetime)}
                      </td>
                      <td className="py-3 px-4 text-xs text-muted-foreground">
                        {f.last_activity ? (
                          <span className="inline-flex items-center gap-1">
                            <Activity className="w-3 h-3" />
                            {new Date(f.last_activity).toLocaleDateString('it-IT')}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="py-3 px-4 text-xs text-muted-foreground">
                        {f.created_at ? new Date(f.created_at).toLocaleDateString('it-IT') : '—'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <ArrowRight className="w-4 h-4 text-muted-foreground/50 inline" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminFamiliesPage;
